import { useState, type FormEvent } from 'react';
import {
  api,
  BladApi,
  type KartaMontazu,
  type NowaPozycjaCzesci,
  type ZmianaMontazu,
} from '../api';
import { doPolaDaty } from '../format';
import { useIdOperacji } from '../operacje';
import { EdytorCzesci } from './EdytorCzesci';
import { Karta, Komunikat, PoleTekstowe, Przycisk, PrzyciskMarki } from './ui';

/**
 * Uzupełnianie zlecenia montażu w trakcie roboty.
 *
 * Druk wypełnia się etapami: przy wystawianiu znany jest klient i rodzaj, podzespoły
 * dobiera magazyn, a **numer fabryczny monter przepisuje z tabliczki dopiero wtedy, gdy
 * maszyna stoi złożona**. Bez tego ekranu ten numer nie miałby jak trafić do systemu.
 *
 * Wysyłamy wyłącznie to, co zmienione — PATCH z całym formularzem nadpisywałby pola,
 * których nikt nie dotknął.
 */
export function FormularzMontazu({
  karta,
  onZapisano,
  onAnuluj,
}: {
  karta: KartaMontazu;
  onZapisano: (karta: KartaMontazu) => void;
  onAnuluj: () => void;
}) {
  const operacja = useIdOperacji();
  const { montaz } = karta;

  const poczatkowe = {
    numerZamowienia: montaz.numerZamowienia ?? '',
    dataRealizacji: doPolaDaty(montaz.dataRealizacji),
    producent: montaz.producent ?? '',
    modelGlowicy: montaz.modelGlowicy ?? '',
    modelNapedu: montaz.modelNapedu ?? '',
    podstawa: montaz.podstawa ?? '',
    blat: montaz.blat ?? '',
    inne: montaz.inne.join('\n'),
    serialNumber: montaz.serialNumber ?? '',
    typDokumentu: montaz.typDokumentu ?? '',
    numerDokumentu: montaz.numerDokumentu ?? '',
    uwagi: montaz.uwagi ?? '',
    uwagiWykonania: montaz.uwagiWykonania ?? '',
  };

  const [wartosci, setWartosci] = useState(poczatkowe);
  const [czesci, setCzesci] = useState<NowaPozycjaCzesci[]>(
    montaz.czesci.map((pozycja) => ({
      numerCzesci: pozycja.numerCzesci ?? undefined,
      opis: pozycja.opis,
      ilosc: Number(pozycja.ilosc),
    })),
  );
  const [blad, setBlad] = useState<string | null>(null);
  const [zapis, setZapis] = useState(false);

  const ustaw = (kod: keyof typeof poczatkowe, wartosc: string): void =>
    setWartosci((poprzednie) => ({ ...poprzednie, [kod]: wartosc }));

  const czesciZmienione =
    JSON.stringify(czesci.filter((p) => p.opis.trim())) !==
    JSON.stringify(
      montaz.czesci.map((p) => ({
        numerCzesci: p.numerCzesci ?? undefined,
        opis: p.opis,
        ilosc: Number(p.ilosc),
      })),
    );

  async function zapisz(zdarzenie: FormEvent): Promise<void> {
    zdarzenie.preventDefault();
    if (zapis) return;

    const zmiany: ZmianaMontazu = {};
    for (const [kod, wartosc] of Object.entries(wartosci)) {
      if (wartosc === poczatkowe[kod as keyof typeof poczatkowe]) continue;
      if (kod === 'inne') {
        zmiany.inne = wartosc
          .split('\n')
          .map((pozycja) => pozycja.trim())
          .filter(Boolean);
      } else {
        (zmiany as Record<string, unknown>)[kod] = wartosc.trim() || undefined;
      }
    }
    if (czesciZmienione) zmiany.czesci = czesci.filter((pozycja) => pozycja.opis.trim());

    if (Object.keys(zmiany).length === 0) {
      onAnuluj();
      return;
    }

    setZapis(true);
    setBlad(null);
    try {
      const nowa = await api.zmienMontaz(montaz.id, {
        ...zmiany,
        clientOperationId: operacja.biezacy(),
      });
      operacja.zakoncz();
      onZapisano(nowa);
    } catch (error: unknown) {
      setBlad(error instanceof BladApi ? error.message : 'Nie udało się zapisać.');
      setZapis(false);
    }
  }

  return (
    <form onSubmit={zapisz} noValidate>
      <Karta tytul="Zamówienie">
        <div className="grid gap-13 sm:grid-cols-2">
          <PoleTekstowe
            etykieta="Numer zamówienia"
            podpowiedz="np. 21/PB/04/2026"
            value={wartosci.numerZamowienia}
            onChange={(z) => ustaw('numerZamowienia', z.target.value)}
            autoComplete="off"
          />
          <PoleTekstowe
            type="date"
            etykieta="Data realizacji"
            value={wartosci.dataRealizacji}
            onChange={(z) => ustaw('dataRealizacji', z.target.value)}
          />
          <PoleTekstowe
            etykieta="Typ dokumentu"
            podpowiedz="proforma, wydanie zewnętrzne"
            value={wartosci.typDokumentu}
            onChange={(z) => ustaw('typDokumentu', z.target.value)}
            autoComplete="off"
          />
          <PoleTekstowe
            etykieta="Numer dokumentu"
            value={wartosci.numerDokumentu}
            onChange={(z) => ustaw('numerDokumentu', z.target.value)}
            autoComplete="off"
          />
        </div>
      </Karta>

      <Karta
        tytul="Wykaz podzespołów"
        akcje={<span className="t-small text-n-500">przejdą na kartę urządzenia</span>}
      >
        <div className="grid gap-13 sm:grid-cols-2">
          <PoleTekstowe
            etykieta="Numer fabryczny"
            podpowiedz="przepisz z tabliczki znamionowej"
            value={wartosci.serialNumber}
            onChange={(z) => ustaw('serialNumber', z.target.value)}
            autoComplete="off"
          />
          <PoleTekstowe
            etykieta="Producent"
            podpowiedz="np. Juki, Brother, Jack"
            value={wartosci.producent}
            onChange={(z) => ustaw('producent', z.target.value)}
            autoComplete="off"
          />
          <PoleTekstowe
            etykieta="Model głowicy"
            value={wartosci.modelGlowicy}
            onChange={(z) => ustaw('modelGlowicy', z.target.value)}
            autoComplete="off"
          />
          <PoleTekstowe
            etykieta="Model napędu"
            value={wartosci.modelNapedu}
            onChange={(z) => ustaw('modelNapedu', z.target.value)}
            autoComplete="off"
          />
          <PoleTekstowe
            etykieta="Podstawa"
            value={wartosci.podstawa}
            onChange={(z) => ustaw('podstawa', z.target.value)}
            autoComplete="off"
          />
          <PoleTekstowe
            etykieta="Blat"
            value={wartosci.blat}
            onChange={(z) => ustaw('blat', z.target.value)}
            autoComplete="off"
          />
        </div>

        <label className="mt-13 block">
          <span className="t-label mb-5 block text-n-700">
            Inne <span className="t-small text-n-500">— każda pozycja w osobnej linii</span>
          </span>
          <textarea
            value={wartosci.inne}
            onChange={(z) => ustaw('inne', z.target.value)}
            rows={3}
            className="w-full border border-n-300 bg-surface px-13 py-9 text-n-900"
          />
        </label>
      </Karta>

      <EdytorCzesci czesci={czesci} onZmiana={setCzesci} />

      <Karta tytul="Uwagi">
        <label className="mb-13 block">
          <span className="t-label mb-5 block text-n-700">Do zlecenia</span>
          <textarea
            value={wartosci.uwagi}
            onChange={(z) => ustaw('uwagi', z.target.value)}
            rows={2}
            className="w-full border border-n-300 bg-surface px-13 py-9 text-n-900"
          />
        </label>
        <label className="block">
          <span className="t-label mb-5 block text-n-700">
            Z wykonania <span className="t-small text-n-500">— co monter zastał i zrobił</span>
          </span>
          <textarea
            value={wartosci.uwagiWykonania}
            onChange={(z) => ustaw('uwagiWykonania', z.target.value)}
            rows={2}
            className="w-full border border-n-300 bg-surface px-13 py-9 text-n-900"
          />
        </label>
      </Karta>

      {blad && (
        <div className="mb-14">
          <Komunikat tresc={blad} />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-13">
        <PrzyciskMarki type="submit" disabled={zapis}>
          {zapis ? 'Zapisywanie…' : 'Zapisz'}
        </PrzyciskMarki>
        <Przycisk type="button" onClick={onAnuluj} disabled={zapis}>
          Anuluj
        </Przycisk>
        <span className="t-small text-n-500">
          Puste pola są w porządku — druk wypełnia się etapami.
        </span>
      </div>
    </form>
  );
}
